/***************************************************************************************************
 * This file handles API calls to interact with the Python backend.
 * It allows for:
 * 
 * 1. Adding and retrieving quotations and submittals.
 * 2. Sending form data to the backend for processing.
 * 3. Updating and fetching real-time information via HTTP requests.
 * 
 * These methods facilitate communication between the React frontend and the Python backend.
 ***************************************************************************************************/

import { cilCloudDownload } from "@coreui/icons";
import axios from "axios";
import { store, authActions } from "src/_store";

const apiUrl = process.env.REACT_APP_API_URL;

/*Hamid 2024-10-25*/
// Configuration de base d'axios
axios.defaults.baseURL = apiUrl;
axios.defaults.withCredentials = true;
//axios.defaults.baseURL = "http://localhost:8000"; // change it when on azure

export { setupAxiosInterceptors };

function setupAxiosInterceptors() {
  axios.interceptors.request.use(
    (config) => {
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response && error.response.status === 401) {
        console.log(" error 401 ==> logout")
        store.dispatch(authActions.logout());
      }
      return Promise.reject(error);
    }
  );
}

export function authenticate(userData) {
  //console.log("Attempting to authenticate user...");

  return axios
    .post(`${apiUrl}/auth/authenticate`, userData)
    .then((response) => {
      //console.log("Authentication successful, response:", response);
      return response.data;
    })
    .catch((error) => {
      if (error.response) {
        //console.error("Server responded with error status:", error.response.status);
        //console.error("Error response data:", error.response.data);

        if (error.response.status === 500) {
          console.error("Internal Server Error - Possible backend issue.");
        } else if (error.response.status === 404) {
          console.error("API endpoint not found - Check backend route.");
        } else if (error.response.status === 403) {
          console.error("Access forbidden - Possibly due to authentication/authorization failure.");
        }
      } else if (error.request) {
        //console.error("No response from server - Check if backend is reachable.");
        //console.error("Request details:", error.request);
      } else {
        //console.error("Error setting up the request:", error.message);
      }
      return Promise.reject(error.response ? error.response.data : error.message);
    });
}


export function sendPassword(email) {
  return axios
    .post(`${apiUrl}/auth/reset-password-with-email`, { email })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function resetPassword(userId) {
  return axios
    .post(`${apiUrl}/auth/reset-password`, { userId })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function setNewPassword({ password, userId /*, token */ }) {
  return axios
    .post(`${apiUrl}/auth/set-password`, { password, userId /*, token */ })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function updatePassword({
  currentPassword,
  password,
  userId /*, token */,
}) {
  return axios
    .post(`${apiUrl}/auth/update-password`, {
      currentPassword,
      password,
      userId /*, token */,
    })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function validateFile(activeProduct, filePath, download) {
  return axios 
    .get(`${apiUrl}/validate-file/${activeProduct}/${encodeURIComponent(filePath)}`, {
      responseType: "blob",
    })
    .then((response) => {

      if (download) {
        // telecharger le fichier avec blob et le bon nom
        const parts = filePath.split("\\");
        const fileName = parts.pop();

        const fileBlob = new Blob([response.data], { type: "application/pdf"});
        const fileURL = URL.createObjectURL(fileBlob);

        // Create an invisible link and set its download attribute
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = fileURL;
        a.download = fileName; // <-- the filename the user will see

        // Append, click, and remove
        document.body.appendChild(a);
        a.click();
        a.remove();
        
        //window.open(fileURL, "_blank")
        setTimeout(() => URL.revokeObjectURL(fileURL), 10000);  // blob cleaning
      }
      else {
        const file_type = filePath.split('.')[1]
        if (file_type == 'json'){
          const fileBlob = new Blob([response.data], { type: "application/json"});
          const fileURL = URL.createObjectURL(fileBlob);
          window.open(fileURL, "_blank")
          setTimeout(() => URL.revokeObjectURL(fileURL), 10000);  // blob cleaning
        }
        else {
          const fileBlob = new Blob([response.data], { type: "application/pdf"});
          const fileURL = URL.createObjectURL(fileBlob);
          window.open(fileURL, "_blank")
          setTimeout(() => URL.revokeObjectURL(fileURL), 10000);  // blob cleaning
        }
      }
    })
    .catch((error) => {
      Promise.reject(error)
    });
}

export function createUser(userData) {
  return axios
    .post(`${apiUrl}/users`, userData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function updateUser(userData) {
  return axios
    .put(`${apiUrl}/users/${userData.id}`, userData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getUser(id) {
  //console.log("GETTING USER'S INFO...");
  return axios
    .get(`${apiUrl}/users/${id}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getUsers() {
  return axios
    .get(`${apiUrl}/users`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function desactivateUser(id) {
  return axios
    .put(`${apiUrl}/users/desactivate/${id}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function reactivateUser(id) {
  return axios
    .put(`${apiUrl}/users/reactivate/${id}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function deleteUser(id) {
  return axios
    .delete(`${apiUrl}/users/delete/${id}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function checkUserQuotationAndRevisions(id) {
  /*console.log(
    `Making API call to check quotations and revisions for user ID: ${id}`
  );*/
  return axios
    .get(`${apiUrl}/users/checkIfHasQuotationORRevision/${id}`)
    .then((response) => {
      //console.log("API Response:", response.data);
      return response.data;
    })
    .catch((error) => {
      //console.log("API Error:", error);
      return Promise.reject(error);
    });
}

export function createTemplate(templateData) {
  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
  };
  return axios
    .post(`${apiUrl}/templates/upload`, templateData, config)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function reactivateTemplate(templateData) {
  return axios
    .post(`${apiUrl}/templates/reactivate`, templateData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function updateTemplate(templateData) {
  return axios
    .put(`${apiUrl}/templates/${templateData.id}`, templateData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getTemplates() {
  return axios
    .get(`${apiUrl}/templates`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getProducts() {
  return axios
    .get(`${apiUrl}/products`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getProductById(productId) {
  return axios
    .get(`${apiUrl}/products/${productId}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getProductsWithLeadTimes() {
  return axios
    .get(`${apiUrl}/products-with-lead-times`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getProductsWithLeadTimesByUser(userId) {
  return axios
    .get(`${apiUrl}/products-with-lead-times/user/${userId}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getLeadTimes() {
  return axios
    .get(`${apiUrl}/lead-times`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getLeadTimesById(leadTimeId) {
  return axios
    .get(`${apiUrl}/lead-times/${leadTimeId}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getLeadTimesByProductId(productId) {
  return axios
    .get(`${apiUrl}/lead-times/products/${productId}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createLeadTime(leadTimeData) {
  return axios
    .post(`${apiUrl}/lead-times`, leadTimeData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function updateLeadTime(leadTimeId, leadTimeData) {
  //console.log("Method in API to update lead time called with ID:", leadTimeId);
  return axios
    .put(`${apiUrl}/lead-times/${leadTimeId}`, leadTimeData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getProjects() {
  return axios
    .get(`${apiUrl}/projects`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createProject(projectData) {
  return axios
    .post(`${apiUrl}/projects`, projectData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function updateProject(projectData) {
  return axios
    .put(`${apiUrl}/projects/${projectData.id}`, projectData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getQuotations() {
  return axios
    .get(`${apiUrl}/quotations`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getQuotation(id) {
  return axios
    .get(`${apiUrl}/quotations/${id}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createABEQuotation(quotationData) {
  return axios
    .post(`${apiUrl}/quotations/ABE`, quotationData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createABE_10_40Quotation(quotationData) {
  return axios
    .post(`${apiUrl}/quotations/ABE_10_40`, quotationData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createSDMEQuotation(quotationData) {
  return axios
    .post(`${apiUrl}/quotations/SDME`, quotationData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createSDMQuotation(quotationData) {
  return axios
    .post(`${apiUrl}/quotations/SDM`, quotationData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createLDEQuotation(quotationData) {
  return axios
    .post(`${apiUrl}/quotations/LDE`, quotationData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createDTHEQuotation(quotationData) {
  return axios
    .post(`${apiUrl}/quotations/DTHE`, quotationData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createSuperblockQuotation(quotationData) {
  return axios
    .post(`${apiUrl}/quotations/Superblock`, quotationData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createABESubmittal(submitalData) {
  return axios
    .post(`${apiUrl}/submittals/ABE`, submitalData /*, { timeout: 10000 }*/)
    .then((response) => {
      //console.log("Received response from API:", response);
      return response.data;
    })
    .catch((error) => {
      //console.error("Error during API request:", error);

      if (error.response) {
        //console.error("Error response data:", error.response.data);
        //console.error("Error response status:", error.response.status);
        //console.error("Error response headers:", error.response.headers);
      } else if (error.request) {
        /*console.error(
          "No response received. Error request data:",
          error.request
        );
        */
      } else {
        //console.error("Unexpected error:", error.message);
      }

      return Promise.reject(error);
    });
}

export function createABE_10_40Submittal(submitalData) {
  return axios
    .post(`${apiUrl}/submittals/ABE_10_40`, submitalData /*, { timeout: 10000 }*/)
    .then((response) => {
      //console.log("Received response from API:", response);
      return response.data;
    })
    .catch((error) => {
      //console.error("Error during API request:", error);

      if (error.response) {
        //console.error("Error response data:", error.response.data);
        //console.error("Error response status:", error.response.status);
        //console.error("Error response headers:", error.response.headers);
      } else if (error.request) {
        /*console.error(
          "No response received. Error request data:",
          error.request
        );
        */
      } else {
        //console.error("Unexpected error:", error.message);
      }

      return Promise.reject(error);
    });
}

export function createSDMESubmittal(submitalData) {
  return axios
    .post(`${apiUrl}/submittals/SDME`, submitalData /*, { timeout: 10000 }*/)
    .then((response) => {
      //console.log("Received response from API:", response);
      return response.data;
    })
    .catch((error) => {
      //console.error("Error during API request:", error);

      if (error.response) {
        //console.error("Error response data:", error.response.data);
        //console.error("Error response status:", error.response.status);
        //console.error("Error response headers:", error.response.headers);
      } else if (error.request) {
        /*console.error(
          "No response received. Error request data:",
          error.request
        );
        */
      } else {
        //console.error("Unexpected error:", error.message);
      }

      return Promise.reject(error);
    });
}

export function createSDMSubmittal(submitalData) {
  return axios
    .post(`${apiUrl}/submittals/SDM`, submitalData /*, { timeout: 10000 }*/)
    .then((response) => {
      //console.log("Received response from API:", response);
      return response.data;
    })
    .catch((error) => {
      //console.error("Error during API request:", error);

      if (error.response) {
        //console.error("Error response data:", error.response.data);
        //console.error("Error response status:", error.response.status);
        //console.error("Error response headers:", error.response.headers);
      } else if (error.request) {
        /*console.error(
          "No response received. Error request data:",
          error.request
        );
        */
      } else {
        //console.error("Unexpected error:", error.message);
      }

      return Promise.reject(error);
    });
}

export function createLDESubmittal(submitalData) {
  return axios
    .post(`${apiUrl}/submittals/LDE`, submitalData /*, { timeout: 10000 }*/)
    .then((response) => {
      //console.log("Received response from API:", response);
      return response.data;
    })
    .catch((error) => {
      //console.error("Error during API request:", error);

      if (error.response) {
        //console.error("Error response data:", error.response.data);
        //console.error("Error response status:", error.response.status);
        //console.error("Error response headers:", error.response.headers);
      } else if (error.request) {
        /*console.error(
          "No response received. Error request data:",
          error.request
        );
        */
      } else {
        //console.error("Unexpected error:", error.message);
      }

      return Promise.reject(error);
    });
}

export function createDTHESubmittal(submitalData) {
  return axios
    .post(`${apiUrl}/submittals/DTHE`, submitalData /*, { timeout: 10000 }*/)
    .then((response) => {
      //console.log("Received response from API:", response);
      return response.data;
    })
    .catch((error) => {
      //console.error("Error during API request:", error);

      if (error.response) {
        //console.error("Error response data:", error.response.data);
        //console.error("Error response status:", error.response.status);
        //console.error("Error response headers:", error.response.headers);
      } else if (error.request) {
        /*console.error(
          "No response received. Error request data:",
          error.request
        );
        */
      } else {
        //console.error("Unexpected error:", error.message);
      }

      return Promise.reject(error);
    });
}

export function createSuperblockSubmittal(submitalData) {
  return axios
    .post(`${apiUrl}/submittals/Superblock`, submitalData /*, { timeout: 10000 }*/)
    .then((response) => {
      //console.log("Received response from API:", response);
      return response.data;
    })
    .catch((error) => {
      //console.error("Error during API request:", error);

      if (error.response) {
        //console.error("Error response data:", error.response.data);
        //console.error("Error response status:", error.response.status);
        //console.error("Error response headers:", error.response.headers);
      } else if (error.request) {
        /*console.error(
          "No response received. Error request data:",
          error.request
        );
        */
      } else {
        //console.error("Unexpected error:", error.message);
      }

      return Promise.reject(error);
    });
}

export function createSuperblockSelection(selectionData) {
  return axios
    .post(`${apiUrl}/createSelection/Superblock`, selectionData /*, { timeout: 10000 }*/)
    .then((response) => {
      //console.log("Received response from API:", response);
      return response.data;
    })
    .catch((error) => {
      //console.error("Error during API request:", error);

      if (error.response) {
        //console.error("Error response data:", error.response.data);
        //console.error("Error response status:", error.response.status);
        //console.error("Error response headers:", error.response.headers);
      } else if (error.request) {
        /*console.error(
          "No response received. Error request data:",
          error.request
        );
        */
      } else {
        //console.error("Unexpected error:", error.message);
      }

      return Promise.reject(error);
    });
}

export function getSuperblockSelections(userInfo) { // !! TODO: admin security check
  return axios
    .get(`${apiUrl}/getSelections/Superblock`, { params: userInfo })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getSuperblockSelectionData(selectionImportCode) {
  return axios
    .get(`${apiUrl}/selectionData/Superblock/${selectionImportCode}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getSubmittals() {
  return axios
    .get(`${apiUrl}/submittals`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getSubmittalsByProduct(productId) {
  return axios
    .get(`${apiUrl}/submittals/${productId}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getSubmittalsByProductAndUserId(productId, userId) {
  return axios
    .get(`${apiUrl}/submittals/${productId}/${userId}`)
    .then((response) => {
      //console.log("Raw submittals data:", response.data); // Log the raw data here
      return response.data;
    })
    .catch((error) => Promise.reject(error));
}

export function getSubmittalsRevisions() {
  return axios
    .get(`${apiUrl}/submittals/revisions`)
    .then((response) => {
      //console.log("Raw revisions data:", response.data); // Log raw data
      return response.data;
    })
    .catch((error) => Promise.reject(error));
}

export function getSubmittalById(id) {
  return axios
    .get(`${apiUrl}/submittalsbyid/${id}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

///////////// AJOUT /////////////
export function getQuotationsAndRevisions(userId) {
  return axios
    .get(`${apiUrl}/submittals/quotations-and-revisions/${userId}`)
    .then((response) => response.data)
    .catch((error) => {
      //console.error("Error fetching quotations and revisions:", error);
      throw error;
    });
}

///////////// AJOUT /////////////
export function getRelatedRecords() {
  return axios
    .get(`${apiUrl}/submittals/related-records`)
    .then((response) => response.data)
    .catch((error) => {
      //console.error("Error fetching related records:", error);
      throw error;
    });
}

export function reactivateSubmittal(id) {
  //console.log("data that will be sent to backend: ", id)
  return axios
    .post(`${apiUrl}/quotations/activate`, id)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function desactivateSubmittal(id) {
  //console.log("data that will be sent to backend: ", id)
  return axios
    .post(`${apiUrl}/quotations/desactivate`, id)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function changeSubmittalStatus(id) {
  return axios
    .put(`${apiUrl}/quotations/status`, id)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function deleteSubmittal(submittalId) {
  // !!TODO: add user security check to deletion
  const quote = submittalId.quote; 
  //console.log("submittalId: ", quote);
  return axios
    .delete(`${apiUrl}/submittals/${quote}`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });
}

export function updateABEQuotation(quotationData) {

  return axios
    .post(`${apiUrl}/revision/ABE`, quotationData)
    .then((response) => response.data)
    .catch((error) => {
      //console.error("Erreur détectée :", error);

      if (error.response && error.response.status === 500) {
        //console.warn("Erreur 500 détectée, redirection en cours...");
      }

      window.location.href = `/#/quotations/${quotationData.Product_id}`;

      return Promise.reject(error);
    });
}

export function updateABE_10_40Quotation(quotationData) {

  return axios
    .post(`${apiUrl}/revision/ABE_10_40`, quotationData)
    .then((response) => response.data)
    .catch((error) => {
      //console.error("Erreur détectée :", error);

      if (error.response && error.response.status === 500) {
        //console.warn("Erreur 500 détectée, redirection en cours...");
      }

      window.location.href = `/#/quotations/${quotationData.Product_id}`;

      return Promise.reject(error);
    });
}

export function updateSDMEQuotation(quotationData) {

  return axios
    .post(`${apiUrl}/revision/SDME`, quotationData)
    .then((response) => response.data)
    .catch((error) => {
      //console.error("Erreur détectée :", error);

      if (error.response && error.response.status === 500) {
        //console.warn("Erreur 500 détectée, redirection en cours...");
      }

      window.location.href = `/#/quotations/${quotationData.Product_id}`;

      return Promise.reject(error);
    });
}

export function updateSDMQuotation(quotationData) {

  return axios
    .post(`${apiUrl}/revision/SDM`, quotationData)
    .then((response) => response.data)
    .catch((error) => {
      //console.error("Erreur détectée :", error);

      if (error.response && error.response.status === 500) {
        //console.warn("Erreur 500 détectée, redirection en cours...");
      }

      window.location.href = `/#/quotations/${quotationData.Product_id}`;

      return Promise.reject(error);
    });
}

export function updateLDEQuotation(quotationData) {

  return axios
    .post(`${apiUrl}/revision/LDE`, quotationData)
    .then((response) => response.data)
    .catch((error) => {
      //console.error("Erreur détectée :", error);

      if (error.response && error.response.status === 500) {
        //console.warn("Erreur 500 détectée, redirection en cours...");
      }

      window.location.href = `/#/quotations/${quotationData.Product_id}`;

      return Promise.reject(error);
    });
}

export function updateDTHEQuotation(quotationData) {

  return axios
    .post(`${apiUrl}/revision/DTHE`, quotationData)
    .then((response) => response.data)
    .catch((error) => {
      //console.error("Erreur détectée :", error);

      if (error.response && error.response.status === 500) {
        //console.warn("Erreur 500 détectée, redirection en cours...");
      }

      window.location.href = `/#/quotations/${quotationData.Product_id}`;

      return Promise.reject(error);
    });
}

export function updateSuperblockQuotation(quotationData) {

  return axios
    .post(`${apiUrl}/revision/Superblock`, quotationData)
    .then((response) => response.data)
    .catch((error) => {
      //console.error("Erreur détectée :", error);

      if (error.response && error.response.status === 500) {
        //console.warn("Erreur 500 détectée, redirection en cours...");
      }

      window.location.href = `/#/quotations/${quotationData.Product_id}`;

      return Promise.reject(error);
    });
}


export function getParameters() {
  return axios
    .get(`${apiUrl}/parameters`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createParameter(parameterData) {
  return axios
    .post(`${apiUrl}/parameters`, parameterData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function updateParameter(parameterData) {
  return axios
    .put(`${apiUrl}/parameters`, parameterData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getParameter(id) {
  return axios
    .get(`${apiUrl}/parameters/${id}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getNotifications() {
  return axios
    .get(`${apiUrl}/notifications/`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getFile(fileData) {
  return axios
    .post(`${apiUrl}/download`, fileData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getCities() {
  return axios
    .get(`${apiUrl}/locations/cities`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getStates() {
  return axios
    .get(`${apiUrl}/locations/states`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getCountries() {
  return axios
    .get(`${apiUrl}/locations/countries`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createCompany(companyData) {
  return axios
    .post(`${apiUrl}/companies`, companyData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function updateCompany(companyData) {
  return axios
    .put(`${apiUrl}/companies`, companyData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getCompanies() {
  return axios
    .get(`${apiUrl}/companies`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function getOffices() {
  return axios
    .get(`${apiUrl}/offices`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function createOffice(officeData) {
  return axios
    .post(`${apiUrl}/offices`, officeData)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}

export function updateOffice(officeData) {
  return axios
    .put(`${apiUrl}/offices/${officeData.id}`, officeData)
    .then((response) => {
      //console.log("API Response from updateOffice:", response.data);
      return { ...officeData, ...response.data.data }; 
    })
    .catch((error) => {
      //console.error("API Error in updateOffice:", error.response?.data || error.message);
      return Promise.reject(error);
    });
}

export function updateUserOffice(userId, officeName) {
  //console.log("userId: ", userId);
  //console.log("officeName: ", officeName);

  return axios
    .put(`${apiUrl}/users/${userId}/${officeName}`)
    .then((response) => response.data)
    .catch((error) => {
      /*console.error(
        "Error updating user office:",
        error.response?.data || error.message
      );*/
      return Promise.reject(error);
    });
}


export function deleteOffice(officeId) {
  return axios
    .delete(`${apiUrl}/offices/${officeId}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
}
